import React, { useEffect, useState, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { Check as IcCheck } from 'lucide-react';
import ReplyEditor from '../components/ReplyEditor';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5001';
const CRIMSON = '#8B1A1A';

export default function Signatures() {
  const ctx = useOutletContext() || {};
  const { clientId, isGlobalMode } = ctx;
  const { getToken } = useAuth();

  const [mailboxes, setMailboxes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [html, setHtml] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [query, setQuery] = useState('');

  const fetchMailboxes = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await getToken();
      const url = isGlobalMode
        ? `${API_BASE}/admin/mailboxes`
        : `${API_BASE}/clients/${clientId}/mailboxes`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(`Request failed (${res.status})`);
      const data = await res.json();
      setMailboxes(Array.isArray(data) ? data : data.mailboxes || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [clientId, isGlobalMode, getToken]);

  useEffect(() => {
    if (isGlobalMode || clientId) fetchMailboxes();
  }, [isGlobalMode, clientId, fetchMailboxes]);

  const selectMailbox = useCallback(async (mb) => {
    setSelectedId(mb.id);
    setSaved(false);
    // Client-mode list carries signature_html; global-mode list does not.
    let sig = mb.signature_html;
    if (sig === undefined) {
      try {
        const token = await getToken();
        const r = await fetch(`${API_BASE}/mailboxes/${mb.id}/signature`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (r.ok) { const d = await r.json(); sig = d.signature_html; }
      } catch (_) { sig = ''; }
      // Write fetched signature back so the list badge reflects reality.
      const fetched = sig;
      setMailboxes((prev) => prev.map((m) => m.id === mb.id ? { ...m, signature_html: fetched || '' } : m));
    }
    setHtml(sig || '');
  }, [getToken]);

  const save = useCallback(async () => {
    if (!selectedId) return;
    setSaving(true);
    setSaved(false);
    try {
      const token = await getToken();
      // Strip TipTap's empty-paragraph artefact so "no signature" stays empty.
      const cleanHtml = (html === '<p></p>') ? '' : html;
      const tmp = document.createElement('div');
      tmp.innerHTML = cleanHtml;
      const text = tmp.textContent || '';
      const r = await fetch(`${API_BASE}/mailboxes/${selectedId}/signature`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ signature_html: cleanHtml, signature_text: text }),
      });
      if (!r.ok) throw new Error(`Save failed (${r.status})`);
      setMailboxes((prev) => prev.map((m) => m.id === selectedId
        ? { ...m, signature_html: cleanHtml, signature_text: text } : m));
      setSaved(true);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }, [selectedId, html, getToken]);

  const filtered = mailboxes.filter((m) => {
    if (!query) return true;
    const q = query.toLowerCase();
    return (m.email_address || '').toLowerCase().includes(q)
      || (m.display_name || '').toLowerCase().includes(q)
      || (m.client?.name || '').toLowerCase().includes(q);
  });

  const selected = mailboxes.find((m) => m.id === selectedId);

  return (
    <div>
      <h1 style={{ margin: 0, fontSize: 24, color: '#111' }}>Signatures</h1>
      <p style={{ color: '#666', marginTop: 6, marginBottom: 20, fontSize: 14 }}>
        Set the signature appended to outbound mail, per mailbox.
      </p>

      {error && (
        <div style={{ background: '#FDEDED', color: '#b91c1c', padding: '10px 14px', borderRadius: 8, marginBottom: 16, fontSize: 14 }}>
          {error}
        </div>
      )}

      <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}>
        <div style={{ width: 300, flexShrink: 0 }}>
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search mailboxes…"
            style={{ width: '100%', padding: '8px 12px', border: '1px solid #E2E2E2', borderRadius: 8, fontSize: 14, marginBottom: 12, boxSizing: 'border-box' }}
          />
          {loading ? (
            <p style={{ color: '#999', fontSize: 14 }}>Loading mailboxes…</p>
          ) : filtered.length === 0 ? (
            <p style={{ color: '#999', fontSize: 14 }}>No mailboxes found.</p>
          ) : (
            <div style={{ border: '1px solid #EEE', borderRadius: 10, overflow: 'hidden' }}>
              {filtered.map((mb) => {
                const isSel = mb.id === selectedId;
                const hasSig = (mb.signature_html || '').trim().length > 0 && mb.signature_html !== '<p></p>';
                return (
                  <button
                    key={mb.id}
                    type="button"
                    onClick={() => selectMailbox(mb)}
                    style={{
                      display: 'block', width: '100%', textAlign: 'left',
                      padding: '12px 14px', border: 'none', borderBottom: '1px solid #F2F2F2',
                      cursor: 'pointer', background: isSel ? '#F1ECEC' : '#fff',
                    }}
                  >
                    <div style={{ fontSize: 14, fontWeight: isSel ? 600 : 500, color: isSel ? CRIMSON : '#222' }}>
                      {mb.display_name || mb.email_address}
                    </div>
                    <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>{mb.email_address}</div>
                    {isGlobalMode && mb.client?.name && (
                      <div style={{ fontSize: 11, color: '#aaa', marginTop: 2 }}>{mb.client.name}</div>
                    )}
                    <div style={{ fontSize: 11, color: hasSig ? '#1a7f3c' : '#bbb', marginTop: 4 }}>
                      {hasSig ? 'Signature set' : 'No signature'}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          {!selected ? (
            <div style={{ padding: '60px 20px', textAlign: 'center', color: '#999', border: '1px dashed #DDD', borderRadius: 12 }}>
              Select a mailbox to edit its signature.
            </div>
          ) : (
            <div>
              <div style={{ fontSize: 14, color: '#444', marginBottom: 12 }}>
                Editing signature for <strong>{selected.email_address}</strong>
              </div>

              <ReplyEditor
                value={html}
                onChange={setHtml}
                placeholder="Compose this mailbox signature…"
                clientId={clientId}
              />

              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 14 }}>
                <button
                  type="button"
                  onClick={save}
                  disabled={saving}
                  style={{
                    padding: '9px 18px', border: 'none', borderRadius: 8, cursor: saving ? 'default' : 'pointer',
                    background: CRIMSON, color: '#fff', fontSize: 14, fontWeight: 600, opacity: saving ? 0.6 : 1,
                  }}
                >
                  {saving ? 'Saving…' : 'Save signature'}
                </button>
                {saved && (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: '#1a7f3c', fontSize: 14 }}>
                    <IcCheck size={16} /> Saved
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
